/**
 * The CenoX mark.
 *
 * A gradient X, drawn rather than imported, so it scales without an asset
 * request on the first paint of the platform's front door.
 */

import { useId, type ReactNode } from 'react';
import { cn } from '../ui';

export function CenoxMark({ className }: { className?: string }): ReactNode {
  /*
    One gradient id per mark. A shared id resolves to the FIRST definition in
    the document — and when that copy sits in a hidden sidebar (every phone),
    every other mark on the page fills with nothing.
  */
  const gradient = `cenox-mark-${useId().replace(/:/g, '')}`;
  return (
    <svg viewBox="0 0 32 32" aria-hidden className={cn('shrink-0', className)}>
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="55%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <path
        d="M6 4 L16 13.5 L26 4 L28 6.5 L18.5 16 L28 25.5 L26 28 L16 18.5 L6 28 L4 25.5 L13.5 16 L4 6.5 Z"
        fill={`url(#${gradient})`}
      />
    </svg>
  );
}

/** The mark with the wordmark beside it, as it appears in the platform header. */
export function CenoxWordmark({ subtitle }: { subtitle?: string }): ReactNode {
  return (
    <span className="flex items-center gap-2">
      <CenoxMark className="size-7" />
      <span className="text-lg font-semibold tracking-tight text-slate-900">
        Ceno
        <span className="bg-gradient-to-r from-brand-600 to-fuchsia-500 bg-clip-text text-transparent">
          X
        </span>
      </span>
      {subtitle && (
        <span className="hidden text-lg font-medium text-slate-400 sm:inline">{subtitle}</span>
      )}
    </span>
  );
}
